import React from 'react'
import { createPortal } from 'react-dom'
import InitialAvatar from 'shared/components/InitialAvatar/InitialAvatar'
import { ReactComponent as CrossIcon } from 'assets/icons/cross.svg'
import { ReactComponent as WarningIcon } from 'assets/icons/warning.svg'
import { ReactComponent as ArrowBackIcon } from 'assets/icons/arrow_back.svg'
import { useAssignModal } from './hooks/useAssignModal'
import s from './styles/AssignModal.module.scss'

const workloadPercent = workload => Math.min(100, Math.round((workload / 20) * 100))

const getWorkloadTone = (workload, counselors) => {
	const loads = counselors.map(item => item.workload)
	const lowest = Math.min(...loads)
	const highest = Math.max(...loads)
	if (highest - lowest <= 2) return 'green'
	if (workload >= highest - 1) return 'red'
	if (workload <= lowest + 1) return 'green'
	return 'orange'
}

const formatGrade = grade => {
	const raw = String(grade || '').trim()
	if (!raw) return ''
	return raw.toLowerCase().startsWith('grade') ? raw : `Grade ${raw}`
}

export const AssignModal = ({
	open,
	selectedStudents = [],
	counselors = [],
	hoveredId,
	onHover,
	onClose,
	onRemoveFromSelection,
	onConfirmAssign,
	isAssigning = false,
}) => {
	const {
		view,
		selectedCounselorId,
		showWarning,
		assignCount,
		reassignmentCount,
		reassignmentStudents,
		goToReassignView,
		goToAssignView,
		removeAssignedFromSelection,
		removeOneFromSelection,
		selectCounselor,
	} = useAssignModal({
		open,
		selectedStudents,
		onRemoveFromSelection,
		onClose,
	})

	if (!open || assignCount === 0) return null

	const selectedCounselor = counselors.find(item => item.id === selectedCounselorId) || null

	const handleConfirm = () => {
		if (!selectedCounselor || isAssigning) return
		onConfirmAssign?.(selectedCounselor)
	}

	const assignView = (
		<>
			<button type="button" className={s.close} onClick={onClose} aria-label="Close">
				<CrossIcon aria-hidden />
			</button>
			<h3 id="assign-counselor-title">Assign {assignCount} students</h3>
			<p>Choose a counselor based on their current workload.</p>

			{showWarning ? (
				<div className={s.warning} role="status">
					<span className={s.warningIcon} aria-hidden>
						<WarningIcon />
					</span>
					<div className={s.warningBody}>
						<strong>
							{reassignmentCount} of {assignCount} selected students already have a
							counselor.
						</strong>
						<span>These students will be reassigned to the new counselor.</span>
					</div>
					<div className={s.warningActions}>
						<button type="button" className={s.btnGhost} onClick={goToReassignView}>
							View students
						</button>
						<button
							type="button"
							className={s.btnDangerOutline}
							onClick={removeAssignedFromSelection}
						>
							Remove assigned
						</button>
					</div>
				</div>
			) : null}

			<div className={s.list}>
				{counselors.map(counselor => {
					const percent = workloadPercent(counselor.workload)
					const projectedPercent = workloadPercent(counselor.workload + assignCount)
					const increase = projectedPercent - percent
					const isPreviewing = hoveredId === counselor.id
					const isSelected = selectedCounselorId === counselor.id
					return (
						<button
							key={counselor.id}
							type="button"
							className={`${s.counselorCard} ${isSelected ? s.counselorSelected : ''}`.trim()}
							onMouseEnter={() => onHover?.(counselor.id)}
							onMouseLeave={() => onHover?.(null)}
							onFocus={() => onHover?.(counselor.id)}
							onBlur={() => onHover?.(null)}
							onClick={() => selectCounselor(counselor.id)}
							aria-pressed={isSelected}
						>
							<span className={s.name}>
								<InitialAvatar name={counselor.name} size={36} />
								<span>
									<strong>{counselor.name}</strong>
									<small>{counselor.workload} students</small>
								</span>
							</span>
							<span className={s.load}>
								<small>Student Workload</small>
								<strong>
									{percent}%{isPreviewing ? <em>+{increase}%</em> : null}
								</strong>
							</span>
							<span
								className={`${s.track} ${isPreviewing ? s.preview : ''}`.trim()}
								data-tone={getWorkloadTone(counselor.workload, counselors)}
							>
								<span className={s.base} style={{ width: `${percent}%` }} />
								<span className={s.projected} style={{ width: `${projectedPercent}%` }} />
							</span>
						</button>
					)
				})}
			</div>

			<div className={s.footer}>
				<button type="button" className={s.btnGhost} onClick={onClose} disabled={isAssigning}>
					Cancel
				</button>
				<button
					type="button"
					className={s.btnPrimary}
					onClick={handleConfirm}
					disabled={!selectedCounselor || isAssigning}
				>
					{isAssigning ? 'Assigning…' : `Assign ${assignCount} students`}
				</button>
			</div>
		</>
	)

	const reassignView = (
		<>
			<button type="button" className={s.close} onClick={onClose} aria-label="Close">
				<CrossIcon aria-hidden />
			</button>
			<button type="button" className={s.back} onClick={goToAssignView}>
				<ArrowBackIcon aria-hidden />
				Back to assignment
			</button>
			<h3 id="assign-counselor-title">
				Students to be reassigned ({reassignmentCount})
			</h3>
			<p>These students already have a counselor and will be reassigned.</p>

			<div className={s.reassignList}>
				{reassignmentStudents.map(student => (
					<div key={student.id} className={s.reassignRow}>
						<span className={s.reassignPerson}>
							<InitialAvatar
								name={student.name}
								avatarKey={student.avatarKey}
								size={34}
							/>
							<span className={s.reassignMeta}>
								<span className={s.reassignNameLine}>
									<strong>{student.name}</strong>
									{formatGrade(student.grade) ? (
										<small>{formatGrade(student.grade)}</small>
									) : null}
								</span>
								<small>
									Current counselor: {student.counselor || 'Assigned'}
								</small>
							</span>
						</span>
						<button
							type="button"
							className={s.rowRemove}
							onClick={() => removeOneFromSelection(student.id)}
							aria-label={`Remove ${student.name} from selection`}
						>
							<CrossIcon aria-hidden />
						</button>
					</div>
				))}
			</div>

			<div className={s.footer}>
				<button
					type="button"
					className={s.btnDangerOutline}
					onClick={removeAssignedFromSelection}
				>
					Remove all assigned
				</button>
				<span className={s.footerCount}>
					{reassignmentCount} {reassignmentCount === 1 ? 'student' : 'students'}
				</span>
			</div>
		</>
	)

	return createPortal(
		<div className={s.backdrop} role="presentation" onMouseDown={onClose}>
			<div
				className={s.modal}
				role="dialog"
				aria-modal="true"
				aria-labelledby="assign-counselor-title"
				onMouseDown={event => event.stopPropagation()}
			>
				{view === 'reassign' ? reassignView : assignView}
			</div>
		</div>,
		document.body,
	)
}

export default AssignModal
